import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ETSY_URL, LOGO_URL, trackEtsy } from "../data/site";
import { ArrowUpRight, Menu, X } from "lucide-react";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const links = [
    { label: "Home", to: "/", type: "route" },
    { label: "Heritage", to: "/heritage", type: "route" },
    { label: "Artisans", to: "/artisans", type: "route" },
    { label: "About", to: "/about", type: "route" },
    { label: "Dresses", to: "/dresses", type: "route", badge: "Soon" },
    { label: "KC-CINEM", to: "/collections/kc-cinem/003-1-the-techcare-reaper", type: "route" },
    {
      label: "Moiré",
      href: "https://moire.live",
      type: "moire",
      section: "navigation",
    },
  ];

  const isActive = (l) => {
    if (l.type !== "route") return false;
    if (l.to === "/") return pathname === "/";
    if (l.label === "KC-CINEM") return pathname.startsWith("/collections/kc-cinem");
    return pathname.startsWith(l.to);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-[#0B0B0B]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-20 py-4 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 shrink-0">
          <img
            src={LOGO_URL}
            alt="Kendi Collections"
            className="h-9 md:h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop */}
        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {links.map((l) => {
            const testid = `nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
            const classes = `text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 ${
              isActive(l) ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
            }`;
            if (l.type === "etsy") {
              return (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEtsy(l.section)}
                  data-testid={testid}
                  className={`${classes} inline-flex items-center gap-1.5`}
                >
                  {l.label}
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              );
            }
            if (l.type === "moire") {
              return (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    typeof window !== "undefined" &&
                    window.trackMoire &&
                    window.trackMoire(l.section, "navigation")
                  }
                  data-testid={`nav-link-moire`}
                  className="text-[11px] tracking-[0.22em] uppercase text-white/50 hover:text-[#D4AF37] transition-colors duration-300 inline-flex items-center gap-1.5 italic font-serif-display"
                  style={{ fontStyle: "italic" }}
                >
                  {l.label}
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              );
            }
            if (l.type === "anchor") {
              return (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() =>
                    typeof window !== "undefined" &&
                    window.trackNavigation &&
                    window.trackNavigation(l.label, l.to, false)
                  }
                  data-testid={testid}
                  className={classes}
                >
                  {l.label}
                </Link>
              );
            }
            return (
              <Link
                key={l.label}
                to={l.to}
                onClick={() =>
                  typeof window !== "undefined" &&
                  window.trackNavigation &&
                  window.trackNavigation(l.label, l.to, false)
                }
                data-testid={testid}
                className={`${classes} inline-flex items-center gap-2`}
              >
                {l.label}
                {l.badge && (
                  <span className="text-[8px] tracking-[0.28em] uppercase text-[#D4AF37]/80 border border-[#D4AF37]/40 px-1.5 py-0.5">
                    {l.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <a
          href={ETSY_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEtsy("nav_shop_heritage")}
          data-testid="navbar-etsy-cta"
          className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-4 py-2.5 hover:bg-[#D4AF37] hover:text-black transition-all duration-300"
        >
          Shop Heritage
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>

        {/* Mobile hamburger */}
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="navbar-mobile-toggle"
          className="md:hidden text-white/85 hover:text-[#D4AF37] p-2"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden border-t border-white/5 bg-[#0B0B0B]/95 backdrop-blur-xl px-6 py-6 flex flex-col gap-4"
        >
          {links.map((l) => {
            const testid = `mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
            if (l.type === "etsy") {
              return (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEtsy(l.section)}
                  data-testid={testid}
                  className="text-[13px] tracking-[0.22em] uppercase text-[#D4AF37] inline-flex items-center gap-2 py-2"
                >
                  {l.label}
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              );
            }
            if (l.type === "moire") {
              return (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    typeof window !== "undefined" &&
                    window.trackMoire &&
                    window.trackMoire(l.section, "navigation")
                  }
                  data-testid="mobile-nav-link-moire"
                  className="text-[13px] tracking-[0.22em] uppercase text-white/70 hover:text-[#D4AF37] inline-flex items-center gap-2 py-2 italic font-serif-display"
                >
                  {l.label}
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              );
            }
            return (
              <Link
                key={l.label}
                to={l.to}
                onClick={() =>
                  typeof window !== "undefined" &&
                  window.trackNavigation &&
                  window.trackNavigation(l.label, l.to, false)
                }
                data-testid={testid}
                className="text-[13px] tracking-[0.22em] uppercase text-white/80 hover:text-[#D4AF37] py-2"
              >
                {l.label}
              </Link>
            );
          })}
        </div>
      )}
    </header>
  );
}
